import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { isAccountBlockedError } from '../lib/accountStatus';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('autoxium_token'));
  const [loading, setLoading] = useState(true);
  const [blockedNotice, setBlockedNotice] = useState(null);

  // Declared BEFORE the initAuth effect on purpose: axios snapshots its interceptor
  // chain when a request starts, so registering this after initAuth fires /users/me
  // would miss that first (page-load) request.
  // An admin can suspend / soft-delete an account while its owner has the site open.
  // The backend then 403s every authenticated call with a fixed detail string; log
  // out on the first one instead of leaving a half-working "logged in" UI. Only
  // reacts to requests that actually carried a token, so the login form's own 403
  // (shown by LoginPage) doesn't trigger it.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (error?.config?.headers?.Authorization && isAccountBlockedError(error)) {
        localStorage.removeItem('autoxium_token');
        setToken(null);
        setUser(null);
        setBlockedNotice(error.response.data.detail);
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Shown only once auth has finished loading: the app doesn't mount <Toaster>
  // until then, and sonner drops toasts fired before it mounts -- which is exactly
  // when the page-load /users/me check hits a blocked account.
  useEffect(() => {
    if (blockedNotice && !loading) {
      toast.error(blockedNotice, { id: 'account-blocked', duration: 8000 });
      setBlockedNotice(null);
    }
  }, [blockedNotice, loading]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('autoxium_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (identifier, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      identifier,
      password
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('autoxium_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    // userData must include verify_token, obtained from verifyOtp() -- the backend
    // rejects registration without proof the email was verified via send-otp/verifyOtp.
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('autoxium_token', newToken);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const sendOtp = async (email) => {
    const response = await axios.post(`${API_URL}/auth/send-otp`, { email });
    return response.data;
  };

  const verifyOtp = async (email, code) => {
    const response = await axios.post(`${API_URL}/auth/verify-otp`, { email, code });
    return response.data.verify_token;
  };

  const logout = () => {
    localStorage.removeItem('autoxium_token');
    setToken(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await axios.post(`${API_URL}/auth/reset-password`, {
      token: resetToken,
      new_password: newPassword
    });
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API_URL}/users/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API_URL}/users/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    sendOtp,
    verifyOtp,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
